import addDays from "date-fns/addDays";
import format from "date-fns-tz/format";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import zonedTimeToUtc from "date-fns-tz/zonedTimeToUtc";
import { czechTimeZone } from "../config";
import { addSeconds, subDays, endOfDay } from "date-fns";
import { isSafari9OrLess } from "./iOS";

const czechTimeZoneDiff = 2;

export const utcToCZZonedTime = (date: Date) => {
  if (isSafari9OrLess) {
    const localTime = date.getTime();
    const czechOffset = czechTimeZoneDiff * 60000;
    const zonedTime = localTime + czechOffset;

    return new Date(zonedTime);
  }

  return utcToZonedTime(date, czechTimeZone);
};

export const zonedCZTimeToUtc = (date: string) => {
  if (isSafari9OrLess) {
    const zonedDate = new Date(date);
    const localTime = zonedDate.getTime();
    const czechOffset = czechTimeZoneDiff * 60000;
    const utc = localTime - czechOffset;
    const zonedTime = utc - 3600000 * czechTimeZoneDiff;

    return new Date(zonedTime);
  }

  return zonedTimeToUtc(date, czechTimeZone);
};

export const toLocalDate = (date: Date, isPozitiveZone: boolean): Date => {
  // 1) Klon, aby originál zůstal nezměněn
  let d = new Date(date);
  // 2) Nastav čas na půlnoc lokálně
  isPozitiveZone ? d.setHours(0, 0, 0, 0) : (d = endOfDay(subDays(d.setHours(0, 0, 1, 0), 1)));
  return d;
};

export const setNowTime = (date: Date): Date => {
  if (date) {
    date.setHours(new Date().getHours(), new Date().getMinutes(), new Date().getSeconds());
  }
  return date;
};

export const endOfCzechDay = (date: Date = new Date()): Date => {
  const czechDate = utcToCZZonedTime(date);
  const dateString = format(czechDate, "yyyy-MM-dd 23:59:59");

  return zonedCZTimeToUtc(dateString);
};

export function startOfPragueDay(date: Date): Date {
  const CZ_TZ = "Europe/Prague";

  // 1) Nejdřív vezmeme dané datum a "převedeme" ho do české zóny
  const pragueDate = utcToZonedTime(date, CZ_TZ);
  // 2) Vyextrahujeme si z něj rok-měsíc-den
  const dayString = format(pragueDate, "yyyy-MM-dd", { timeZone: CZ_TZ });
  // 3) Sestavíme ISO řetězec půlnoci v ČR a převedeme ho na UTC Date
  return zonedTimeToUtc(`${dayString}T00:00:00`, CZ_TZ);
}

export const dateToIsoDate = (date: Date): string => {
  const z = utcToZonedTime(date, czechTimeZone);
  return format(z, "yyyy-MM-dd", { timeZone: czechTimeZone });
};

export const dateToIsoString = (date: Date): string => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Porovná dva ISO‐stringy, jestli padnou na stejný kalendářní den v ČR.
 */
export const isSameOrBeforePragueDay = (a: Date, b: Date): boolean => {
  return dateToIsoDate(a) <= dateToIsoString(b);
};

export const isSamePragueDay = (a: Date, b: Date): boolean => {
  return dateToIsoDate(a) <= dateToIsoString(b);
};

export const isBeforePragueDay = (a: Date, b: Date): boolean => {
  return dateToIsoDate(a) < dateToIsoString(b);
};

export const dateStringWithServerZone = (dataDate: Date, serverTime: Date): Date => {
  // 1) extrahujeme YYYY-MM-DD z dataDate
  const y = dataDate.getFullYear();
  const m = String(dataDate.getMonth() + 1).padStart(2, "0");
  const d = String(dataDate.getDate()).padStart(2, "0");
  const dateString = `${y}-${m}-${d}`; // např. "2025-05-25"

  // 2) spočteme offset serverTime v minutách (UTC→local)
  const tzOffsetMin = -serverTime.getTimezoneOffset();
  const sign = tzOffsetMin >= 0 ? "+" : "-";
  const absMin = Math.abs(tzOffsetMin);
  const hh = String(Math.floor(absMin / 60)).padStart(2, "0");
  const mm = String(absMin % 60).padStart(2, "0");

  // 3) složíme ISO midnight string s tím offsetem
  const iso = `${dateString}T00:00:00${sign}${hh}:${mm}`;

  // 4) new Date(iso) tajně převede na interní UTC čas
  return new Date(iso);
};

export function parseServerTimeToLocal(serverTime: string): Date {
  // 1) Nejprve si parsni string na UTC Date (interně to uloží epoch ms)
  const utcDate = new Date(serverTime);

  // 2) Zjisti klientovu TZ (např. "Europe/Prague" nebo cokoliv jiného)
  const clientTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // 3) Přepočti tu UTC date na date, která při volání getHours() atd.
  //    vrátí hodnotu v clientTZ
  return utcToZonedTime(utcDate, clientTZ);
}

export const startOfCzechDay = (date: Date = new Date()): Date => {
  return addSeconds(subDays(endOfCzechDay(date), 1), 1);
};

export const getTimeZoneLabel = (date: Date): string => {
  const formatted = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Prague",
    timeZoneName: "short",
  }).format(date);
  if (formatted.includes("CEST")) return "CEST";
  if (formatted.includes("CET")) return "CET";
  return "CET";
};
export const edazAddDays = (date: Date, amount: number) => {
  if (isSafari9OrLess) {
    const dateToAddDays = date;
    dateToAddDays.setDate(dateToAddDays.getDate() + amount);
    return dateToAddDays;
  }

  return addDays(date, amount);
};

export const czechTodayDate = () => utcToCZZonedTime(new Date());

export const disabledDaysRange = (before?: Date | null, paidDate?: Date | null, maxValidAmount = 30) => ({
  before: before ? before : czechTodayDate(),
  after: edazAddDays(paidDate || czechTodayDate(), maxValidAmount),
});

export const minValidSince = (serverTime?: Date) => addSeconds(endOfCzechDay(serverTime ?? new Date()), 1);

export const maxValidSince = (paidDate?: Date | null, maxValidAmount = 30) => {
  return edazAddDays(paidDate || new Date(), maxValidAmount);
};
